import React, { useState } from 'react';
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import GridContainer from 'components/GridContainer';
import TextField from 'components/FormElements/AppTextInput';
import FieldRow from 'components/FormElements/FieldRow';
import CkEditor from 'components/CkEditor';
import { useInvestorReportingFormContext } from 'contexts/InvestorReportingFormContext';
import { Controller } from 'react-hook-form';
import { stripTags } from 'hooks/form/useInvestorReportingForm';
import Spacing from 'components/Spacing';

type Props = any;

const ManagerOverViewForm: React.FC<Props> = ({
  classes,
  disabled,
  setValue,
  control,
  summaryClassName,
}) => {
  const { managerOverview } = useInvestorReportingFormContext();
  const [provideCommentary, setProvideCommentary] = useState(
    !!managerOverview.managerOverViewProvideCommentary,
  );
  const [shortDescriptionCharacterCount, setShortDescriptionCharacterCount] =
    useState(0);
  const shortDescriptionCharacterLimit = 220;
  const shortDescriptionCharacterError =
    shortDescriptionCharacterCount > shortDescriptionCharacterLimit;

  return (
    <>
      <Accordion className={classes.customAccordion} expanded={true}>
        <AccordionSummary
          className={summaryClassName}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <Typography className={classes.accordionHeader}>
            Manager’s overview (used for PDF updates, system emails, advisers
            and syndicates)
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.cmAccDetail}>
          <GridContainer>
            <Grid
              size={{
                md: 9,
                xs: 12,
              }}
            >
              <Spacing>
                <FieldRow title="Update title">
                  <Controller
                    name="managerOverview[managerOverViewTitle]"
                    defaultValue={managerOverview.managerOverViewTitle}
                    control={control}
                    render={({
                      field: { ref, ...rest },
                      formState: { errors },
                    }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        required
                        placeholder="e.g. Fund name - Q1 Quarterly Update"
                        fullWidth
                        //@ts-expect-error
                        error={!!errors?.managerOverview?.managerOverViewTitle}
                        helperText={
                          //@ts-expect-error
                          errors?.managerOverview?.managerOverViewTitle?.message
                        }
                        disabled={disabled}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow
                  title="Short description"
                  informationText="This is a two line preview of the update that will show in each investor’s portal before they open the update."
                  topTitle
                >
                  <Controller
                    name="managerOverview[managerOverViewShortDescription]"
                    defaultValue={
                      managerOverview.managerOverViewShortDescription
                    }
                    control={control}
                    render={({ field, formState: { errors } }) => (
                      <>
                        <CkEditor
                          placeholder="A brief 30-35 word (220 character) preview of fund performance and what the update contains."
                          showLists={false}
                          onChange={(value: string) => {
                            setShortDescriptionCharacterCount(
                              stripTags(value ?? '').length,
                            );
                            setValue(
                              'managerOverview[managerOverViewShortDescription]',
                              value,
                            );
                          }}
                          onBlur={(value: string) => {
                            setValue(
                              'managerOverview[managerOverViewShortDescription]',
                              value,
                            );
                          }}
                          value={field.value}
                          disabled={disabled}
                        />
                        <p
                          className={classes.errorCk}
                          id="managerOverview[managerOverViewShortDescription]"
                        >
                          {
                            errors?.managerOverview /* @ts-expect-error */
                              ?.managerOverViewShortDescription?.message
                          }
                        </p>
                        {shortDescriptionCharacterCount > 0 && (
                          <p
                            className={`${classes.characterCount} ${
                              shortDescriptionCharacterError &&
                              classes.characterCountError
                            }`}
                          >
                            Character count: {shortDescriptionCharacterCount}{' '}
                            {shortDescriptionCharacterError &&
                              `(Character count must be ${shortDescriptionCharacterLimit} or less)`}
                          </p>
                        )}
                      </>
                    )}
                  />
                </FieldRow>
                <FieldRow title="Provide commentary">
                  <Controller
                    name="managerOverview[managerOverViewProvideCommentary]"
                    defaultValue={
                      !!managerOverview.managerOverViewProvideCommentary
                    }
                    control={control}
                    render={({ field: { name, value } }) => (
                      <Switch
                        name={name}
                        onChange={(event) => {
                          setValue(
                            'managerOverview[managerOverViewProvideCommentary]',
                            event.target.checked,
                          );
                          setProvideCommentary(event.target.checked);
                        }}
                        className={classes.switchCls}
                        checked={!!value}
                        disabled={disabled}
                      />
                    )}
                  />
                </FieldRow>
                {provideCommentary ? (
                  <>
                    <FieldRow
                      title="Manager commentary and sign-off"
                      informationText="This is your firm’s introduction, where you can introduce the update and discuss relevant topics. The system will automatically add “Dear {{name}}” at the beginning. You should add the sign-off you feel appropriate."
                      topTitle
                    >
                      <Controller
                        name="managerOverview[managerOverViewCommentary]"
                        defaultValue={
                          managerOverview.managerOverViewCommentary ?? ''
                        }
                        control={control}
                        render={({ field, formState: { errors } }) => (
                          <>
                            <CkEditor
                              placeholder="A multi-paragraph overview of progress since the last update."
                              onChange={(value: string) => {
                                setValue(
                                  'managerOverview[managerOverViewCommentary]',
                                  value,
                                );
                              }}
                              value={field.value}
                              disabled={disabled}
                              onBlur={(value: string) => {
                                setValue(
                                  'managerOverview[managerOverViewCommentary]',
                                  value,
                                );
                              }}
                            />
                            <p
                              className={classes.errorCk}
                              id="managerOverview[managerOverViewCommentary]"
                            >
                              {
                                errors?.managerOverview /* @ts-expect-error */
                                  ?.managerOverViewCommentary?.message
                              }
                            </p>
                          </>
                        )}
                      />
                    </FieldRow>
                  </>
                ) : null}
              </Spacing>
            </Grid>
          </GridContainer>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default ManagerOverViewForm;
